/**
 * AccountsManagerComponent
 *
 * A component that provides the main administrative interface for managing user accounts
 * in the FeedMe Sales Panel application. This component serves as a container for user
 * management functionality and includes a responsive sidebar navigation.
 *
 * Features:
 * - Responsive sidebar navigation with mobile support
 * - User management interface
 * - Secure logout functionality
 * - Mobile-friendly design with animated drawer
 * - Modern UI with gradient backgrounds and glassmorphism effects
 * - Smooth animations and transitions
 *
 * @example
 * ```html
 * <app-accounts-manager>
 *   <!-- Admin interface will be rendered here -->
 * </app-accounts-manager>
 * ```
 */
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/auth.service';
import { UsersListComponent } from '../users-list/users-list.component';
import { FormCompilationComponent } from '../form-compilation/form-compilation.component';
import { Subject } from 'rxjs';
import { RouterModule } from '@angular/router';
import Swal from 'sweetalert2';
import { CustomerFormComponent } from '../../features/customer-management/customer-form/customer-form.component';

/** Type definition for navigation tabs */
type TabType = 'users' | 'form';

@Component({
  selector: 'app-accounts-manager',
  standalone: true,
  imports: [
    CommonModule,
    UsersListComponent,
    RouterModule,
    CustomerFormComponent,
  ],
  template: `
    <div class="flex h-screen bg-slate-50">
      <!-- Sidebar for desktop -->
      <aside class="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 shadow-lg transition-all duration-300 animate-fade-in" style="animation-delay: 0ms">
        <!-- Logo Section -->
        <div class="flex items-center justify-center h-28 px-6 border-b border-slate-200 bg-gradient-to-r from-secondary to-secondary-dark animate-fade-in" style="animation-delay: 100ms">
          <img src="assets/LOGO.png" alt="Company Logo" class="h-24 w-auto transition-transform duration-300 hover:scale-105">
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-4 py-6 space-y-2 overflow-y-auto animate-fade-in" style="animation-delay: 200ms">
          <button (click)="showCustomerUserList()"
            class="flex items-center w-full px-4 py-3.5 text-base font-medium rounded-xl transition-all duration-200 ease-in-out group bg-white hover:bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md animate-fade-in"
            style="animation-delay: 300ms"
          >
            <div class="flex items-center">
              <div class="p-2 rounded-lg bg-secondary/10 mr-3">
                <svg
                  class="w-5 h-5 text-secondary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <span class="text-gray-800 font-semibold">Utenti</span>
            </div>
          </button>
          <button (click)="showCustomerForm()"
            class="flex items-center w-full px-4 py-3.5 text-base font-medium rounded-xl transition-all duration-200 ease-in-out group bg-white hover:bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md animate-fade-in"
            style="animation-delay: 300ms"
          >
            <div class="flex items-center">
              <div class="p-2 rounded-lg bg-secondary/10 mr-3">
                <svg width="20px" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 18L17 18M17 18L14 18M17 18V15M17 18V21M11 21H4C4 17.134 7.13401 14 11 14C11.695 14 12.3663 14.1013 13 14.2899M15 7C15 9.20914 13.2091 11 11 11C8.79086 11 7 9.20914 7 7C7 4.79086 8.79086 3 11 3C13.2091 3 15 4.79086 15 7Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>   </div>
              <span class="text-gray-800 font-semibold">Add Utenti</span>
            </div>
          </button>
        </nav>

        <!-- User Section -->
        <div class="flex flex-col px-4 py-4 border-t border-slate-200 bg-slate-50 animate-fade-in" style="animation-delay: 400ms">
          <div class="flex items-center px-3 py-3 mb-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-all duration-200">
            <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-secondary to-secondary-dark">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-slate-700">Francesca</p>
              <p class="text-xs text-slate-500">Amministratore</p>
            </div>
          </div>
          <button
            (click)="confirmLogout()"
            class="flex items-center justify-center w-full px-3 py-2.5 text-sm font-medium text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary rounded-lg transition-all duration-200 hover:shadow-lg"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </aside>

      <!-- Mobile Header -->
      <div class="fixed top-0 left-0 right-0 z-40 md:hidden animate-fade-in" style="animation-delay: 200ms">
        <div class="bg-gradient-to-r from-secondary to-secondary-dark shadow-sm">
          <div class="flex items-center justify-between h-20 px-4">
            <button
              (click)="toggleMobileMenu()"
              class="inline-flex items-center justify-center p-2 rounded-xl text-white hover:text-primary hover:bg-white/10 transition-all duration-200"
            >
              <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div class="flex items-center justify-center flex-1">
              <div class="relative h-16 w-auto flex items-center">
                <span class="ml-3 text-white text-lg font-medium">Pannello Admin</span>
              </div>
            </div>

            <div class="w-11"></div>
          </div>
        </div>
      </div>

      <!-- Mobile Menu Drawer -->
      <div class="fixed inset-0 z-50 md:hidden" [style.visibility]="isMobileMenuOpen ? 'visible' : 'hidden'">
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300"
          [class.opacity-100]="isMobileMenuOpen"
          [class.opacity-0]="!isMobileMenuOpen"
          (click)="toggleMobileMenu()"
        ></div>

        <!-- Menu Panel -->
        <div
          class="absolute inset-y-0 left-0 flex flex-col w-[280px] bg-white shadow-lg transition-all duration-300"
          [class.translate-x-0.opacity-100]="isMobileMenuOpen"
          [class.-translate-x-full.opacity-0]="!isMobileMenuOpen"
        >
          <!-- Header -->
          <div class="flex items-center justify-between h-28 px-6 border-b border-slate-200 bg-gradient-to-r from-secondary to-secondary-dark">
            <img src="assets/LOGO.png" alt="Company Logo" class="h-20 transition-transform duration-300 hover:scale-105">
            <button
              (click)="toggleMobileMenu()"
              class="p-2 text-white rounded-lg hover:bg-white/10 transition-colors duration-200"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Navigation Menu -->
          <nav class="flex-1 px-4 py-6">
            <div class="space-y-2">
              <button
                class="flex items-center w-full px-4 py-3.5 text-base font-medium rounded-xl transition-all duration-200 ease-in-out group bg-white hover:bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md"
              >
                <div class="flex items-center">
                  <div class="p-2 rounded-lg bg-secondary/10 mr-3">
                    <svg
                      class="w-5 h-5 text-secondary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <span class="text-gray-800 font-semibold">Utenti</span>
                </div>
              </button>
            </div>
          </nav>

          <!-- User Section -->
          <div class="px-4 py-4 border-t border-slate-200 bg-slate-50">
            <div class="flex items-center px-3 py-3 mb-3 rounded-xl bg-white shadow-sm hover:shadow-md transition-all duration-200">
              <div class="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-secondary to-secondary-dark">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm font-medium text-slate-700">Francesca</p>
                <p class="text-xs text-slate-500">Amministratore</p>
              </div>
            </div>
            <button
              (click)="confirmLogout()"
              class="flex items-center justify-center w-full px-3 py-2.5 text-sm font-medium text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary rounded-lg transition-all duration-200 hover:shadow-lg"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <main class="flex-1 overflow-y-auto bg-slate-50 md:pt-0 pt-14">
        <!-- Users List -->
        <div class="px-6 mt-6">
          <div class="animate-fade-in" style="animation-delay: 500ms">
            <ng-container *ngIf="activeComponent === 'list'; else showForm">
            <app-users-list />
            </ng-container>
            <ng-template #showForm>
              <app-customer-form></app-customer-form>
            </ng-template>
          </div>
        </div>

        <!-- Pagination -->
        <div class="mt-6 flex justify-center pb-6 animate-fade-in" style="animation-delay: 600ms">
          <!-- ... pagination content ... -->
        </div>
      </main>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100vh;
      width: 100vw;
    }

    /* Modern scrollbar */
    ::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    ::-webkit-scrollbar-track {
      background: transparent;
    }

    ::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb:hover {
      background: #94a3b8;
    }

    /* Hide scrollbar for mobile */
    @media (max-width: 768px) {
      ::-webkit-scrollbar {
        width: 0;
        height: 0;
      }
    }

    /* Prevent content shift when scrollbar appears */
    .overflow-y-auto {
      scrollbar-gutter: stable;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
        transform: translateY(10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .animate-fade-in {
      opacity: 0;
      animation: fadeIn 0.5s ease-out forwards;
    }
  `]
})
export class AccountsManagerComponent implements OnInit, OnDestroy {

  activeComponent: 'list' | 'form' = 'list';

  /** Currently active navigation tab */
  activeTab: TabType = 'users';

  /** Flag to control mobile menu visibility */
  isMobileMenuOpen = false;

  /** Subject for handling component cleanup */
  private destroy$ = new Subject<void>();

  /** Array of user data to be displayed */
  users: any[] = [];

  constructor(
    /** Service for handling authentication-related operations */
    public authService: AuthService
  ) { }

  /**
   * Lifecycle hook that initializes the component
   * Sets up any necessary subscriptions or initial state
   */
  ngOnInit(): void {
    this.activeTab = 'users';
    this.users = [];
  }

  /**
   * Lifecycle hook that cleans up the component
   * Unsubscribes from all subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Toggles the visibility of the mobile menu drawer
   * Used for responsive navigation on mobile devices
   */
  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  showCustomerForm() {
    this.activeComponent = 'form';
  }

  showCustomerUserList() {
    this.activeComponent = 'list';
  }

  /**
   * Handles the logout process with confirmation
   * Shows a confirmation dialog using SweetAlert2 before logging out
   *
   * @returns Promise that resolves when the logout process is complete
   */
  async confirmLogout(): Promise<void> {
    const result = await Swal.fire({
      title: 'Conferma Logout',
      text: 'Sei sicuro di voler effettuare il logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sì, esci',
      cancelButtonText: 'Annulla',
      customClass: {
        container: 'font-sans',
        popup: 'rounded-2xl shadow-2xl border-t border-gray-100',
        title: 'text-xl font-semibold text-gray-800 px-6 py-4 border-b border-gray-100',
        closeButton: 'focus:outline-none focus:ring-2 focus:ring-secondary',
        htmlContainer: 'text-base text-gray-600 px-6 py-4',
        actions: 'px-6 py-4 flex gap-4 sm:gap-6',
        confirmButton: 'w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-white bg-secondary hover:bg-secondary-dark rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary',
        cancelButton: 'w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 sm:mt-0',
      },
      buttonsStyling: false,
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        await this.authService.logout();
        await Swal.fire({
          title: 'Logout Effettuato',
          text: 'Hai effettuato il logout con successo.',
          icon: 'success',
          customClass: {
            container: 'font-sans',
            popup: 'rounded-2xl shadow-2xl border-t border-gray-100',
            title: 'text-xl font-semibold text-gray-800 px-6 py-4 border-b border-gray-100',
            closeButton: 'focus:outline-none focus:ring-2 focus:ring-secondary',
            htmlContainer: 'text-base text-gray-600 px-6 py-4',
            actions: 'px-6 py-4',
            confirmButton: 'w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-white bg-secondary hover:bg-secondary-dark rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary',
          },
          buttonsStyling: false
        });
      } catch (error) {
        console.error('Error during logout:', error);
        await Swal.fire({
          title: 'Errore',
          text: 'Si è verificato un errore durante il logout.',
          icon: 'error',
          customClass: {
            container: 'font-sans',
            popup: 'rounded-2xl shadow-2xl border-t border-gray-100',
            title: 'text-xl font-semibold text-gray-800 px-6 py-4 border-b border-gray-100',
            closeButton: 'focus:outline-none focus:ring-2 focus:ring-secondary',
            htmlContainer: 'text-base text-gray-600 px-6 py-4',
            actions: 'px-6 py-4',
            confirmButton: 'w-full sm:w-auto px-6 py-2.5 text-sm font-semibold text-white bg-secondary hover:bg-secondary-dark rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary',
          },
          buttonsStyling: false
        });
      }
    }
  }
}
